import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ApiError,
  requestPasswordReset,
  confirmPasswordReset,
} from "@/lib/api";
import {
  Lock,
  User as UserIcon,
  Eye,
  EyeOff,
  Mail,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  X,
  ArrowRight,
} from "lucide-react";

const BRAND = "#4f46e5";
const BRAND_HOVER = "#4338ca";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState<"fr" | "en">("fr");
  const [resetOpen, setResetOpen] = useState(false);

  const t = lang === "fr" ? FR : EN;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const me = await auth.login(username, password);
      if (me.must_change_password) {
        navigate("/change-password", { replace: true });
        return;
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError(t.networkError);
    } finally {
      setLoading(false);
    }
  };

  if (auth.status === "authenticated") {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div
      className="relative min-h-screen min-h-[100svh] flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        background:
          "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
      }}
    >
      {/* Discreet grid backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 40%, black 40%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 40%, black 40%, transparent 85%)",
        }}
      />

      {/* Header */}
      <header className="relative z-10 w-full px-5 sm:px-8 pt-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img
            src="/moko-logo.png"
            alt="Moko Afrika"
            className="w-8 h-8 rounded-md object-contain"
          />
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-slate-900 tracking-tight">
              Moko Afrika
            </div>
            <div className="text-[10.5px] text-slate-500 tracking-wide">
              FreshPay POS
            </div>
          </div>
        </div>
        <LanguageSwitcher lang={lang} onChange={setLang} />
      </header>

      {/* Center card */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[420px]">
          <div
            className="bg-white rounded-2xl border border-slate-200/70 px-6 sm:px-8 py-8"
            style={{
              boxShadow:
                "0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -8px rgba(15, 23, 42, 0.10)",
            }}
          >
            {/* Title block */}
            <div className="mb-7">
              <h1 className="text-[24px] font-semibold text-slate-900 tracking-tight">
                {t.title}
              </h1>
              <p className="text-[13.5px] text-slate-500 mt-1">
                {t.subtitle}
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <Field label={t.username}>
                <div className="relative">
                  <UserIcon
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="input pl-9"
                  />
                </div>
              </Field>

              <Field
                label={t.password}
                trailing={
                  <button
                    type="button"
                    onClick={() => setResetOpen(true)}
                    className="text-[12px] font-medium text-slate-500 hover:text-indigo-700 transition-colors"
                  >
                    {t.forgot}
                  </button>
                }
              >
                <div className="relative">
                  <Lock
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="input pl-9 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? t.hidePassword : t.showPassword}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 rounded-md"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </Field>

              {error && (
                <div className="text-[13px] text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-md text-white text-[14px] font-medium transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 mt-1"
                style={{
                  background: BRAND,
                  boxShadow:
                    "0 1px 2px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(79, 70, 229, 0.28)",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = BRAND_HOVER)
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = BRAND)}
              >
                {loading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t.loading}
                  </>
                ) : (
                  <>
                    {t.button}
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </form>

            {/* Divider + support */}
            <div className="mt-7 pt-5 border-t border-slate-100 flex items-center justify-between">
              <div className="text-[12px] text-slate-500">{t.needHelp}</div>
              <a
                href="mailto:support@gofreshbakery.com"
                className="text-[12px] font-medium text-slate-700 hover:text-indigo-700 transition-colors inline-flex items-center gap-1.5"
              >
                <Mail size={12} />
                support@gofreshbakery.com
              </a>
            </div>
          </div>

          {/* Footer legal */}
          <div className="mt-5 text-center text-[11px] text-slate-400">
            © {new Date().getFullYear()} Moko Afrika · Dashboard marchand
          </div>
        </div>
      </main>

      {resetOpen && (
        <PasswordResetModal
          lang={lang}
          initialUsername={username}
          onClose={() => setResetOpen(false)}
          onSuccess={(uname) => {
            setResetOpen(false);
            setUsername(uname);
            setPassword("");
            setError(null);
          }}
        />
      )}

      <style>{`
        .input {
          width: 100%;
          padding: 0.625rem 0.875rem;
          background-color: #f8fafc;
          border: 1px solid #cbd5e1;
          border-radius: 0.5rem;
          color: #0f172a;
          font-size: 14px;
          line-height: 1.4;
          transition: border-color 120ms, box-shadow 120ms, background-color 120ms;
        }
        .input::placeholder { color: #94a3b8; }
        .input:hover { border-color: #94a3b8; }
        .input:focus {
          outline: none;
          border-color: ${BRAND};
          background-color: #ffffff;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.14);
        }
        .input.pl-9 { padding-left: 2.5rem; }
        .input.pl-10 { padding-left: 2.75rem; }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Field({
  label,
  children,
  trailing,
}: {
  label: string;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-[12.5px] font-medium text-slate-700">{label}</label>
        {trailing}
      </div>
      {children}
    </div>
  );
}

function LanguageSwitcher({
  lang,
  onChange,
}: {
  lang: "fr" | "en";
  onChange: (l: "fr" | "en") => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white/80 backdrop-blur-sm p-0.5">
      {(["fr", "en"] as const).map((l) => (
        <button
          key={l}
          onClick={() => onChange(l)}
          className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider rounded transition-colors ${
            lang === l
              ? "bg-slate-900 text-white"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Password reset modal
// ---------------------------------------------------------------------------
function PasswordResetModal({
  lang,
  initialUsername,
  onClose,
  onSuccess,
}: {
  lang: "fr" | "en";
  initialUsername: string;
  onClose: () => void;
  onSuccess: (username: string) => void;
}) {
  const t = lang === "fr" ? FR.reset : EN.reset;
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [ident, setIdent] = useState(initialUsername);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const requestCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await requestPasswordReset(ident.trim());
      setStep(2);
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail || e.title : t.networkError);
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setErr(t.mismatch);
      return;
    }
    if (newPassword.length < 8) {
      setErr(t.tooShort);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await confirmPasswordReset(code.trim(), newPassword);
      setStep(3);
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail || e.title : t.networkError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-[2px]">
      <div
        className="w-full max-w-[440px] bg-white rounded-xl border border-slate-200 overflow-hidden"
        style={{
          boxShadow:
            "0 20px 40px -20px rgba(15, 23, 42, 0.35), 0 4px 12px -4px rgba(15, 23, 42, 0.10)",
        }}
      >
        <div className="flex items-start justify-between px-5 sm:px-6 py-4 border-b border-slate-100">
          <div>
            <div className="text-[14.5px] font-semibold text-slate-900 tracking-tight">
              {step === 1 && t.step1Title}
              {step === 2 && t.step2Title}
              {step === 3 && t.step3Title}
            </div>
            {step !== 3 && (
              <div className="text-[11px] text-slate-400 mt-0.5 tracking-wide">
                {t.stepLabel(step, 2)}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
            aria-label={t.close}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 sm:p-6">
          {step === 1 && (
            <form onSubmit={requestCode} className="space-y-4">
              <p className="text-[13px] text-slate-600 leading-relaxed">
                {t.step1Intro}
              </p>
              <Field label={t.identLabel}>
                <div className="relative">
                  <Mail
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    required
                    autoFocus
                    value={ident}
                    onChange={(e) => setIdent(e.target.value)}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="input pl-9"
                    placeholder={t.identPlaceholder}
                  />
                </div>
              </Field>

              {err && <ErrorLine text={err} />}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="flex-1 py-2 rounded-md border border-slate-200 text-slate-700 text-[13px] font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={busy || !ident.trim()}
                  className="flex-1 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2 transition"
                  style={{ background: BRAND }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = BRAND_HOVER)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = BRAND)
                  }
                >
                  {busy && <Loader2 size={13} className="animate-spin" />}
                  {t.sendCode}
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={confirmCode} className="space-y-4">
              <p className="text-[13px] text-slate-600 leading-relaxed">
                {t.step2Intro}
              </p>

              <Field label={t.codeLabel}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  required
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="input text-center font-mono tracking-[0.4em] text-base"
                  placeholder="000000"
                />
              </Field>

              <Field label={t.newPasswordLabel}>
                <div className="relative">
                  <Lock
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input pl-9 pr-10"
                    placeholder={t.newPasswordPlaceholder}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? t.hidePassword : t.showPassword}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 rounded-md"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </Field>

              <Field label={t.confirmLabel}>
                <div className="relative">
                  <Lock
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pl-9"
                    placeholder={t.confirmPlaceholder}
                  />
                </div>
              </Field>

              {err && <ErrorLine text={err} />}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setErr(null);
                  }}
                  disabled={busy}
                  className="py-2 px-3 rounded-md border border-slate-200 text-slate-700 text-[13px] font-medium hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <ArrowLeft size={13} />
                  {t.back}
                </button>
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="flex-1 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2 transition"
                  style={{ background: BRAND }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = BRAND_HOVER)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = BRAND)
                  }
                >
                  {busy && <Loader2 size={13} className="animate-spin" />}
                  {t.confirm}
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="py-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 size={20} className="text-emerald-600" />
                </div>
                <div>
                  <div className="text-[14.5px] font-semibold text-slate-900">
                    {t.doneTitle}
                  </div>
                  <div className="text-[13px] text-slate-500">
                    {t.doneSubtitle}
                  </div>
                </div>
              </div>
              <button
                onClick={() => onSuccess(ident.includes("@") ? "" : ident.trim())}
                className="w-full py-2 rounded-md text-white text-[13px] font-medium transition"
                style={{ background: BRAND }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = BRAND_HOVER)
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = BRAND)}
              >
                {t.doneCta}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <div className="text-[12.5px] text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-1.5">
      {text}
    </div>
  );
}

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------
const FR = {
  title: "Connexion",
  subtitle: "Accédez à votre dashboard marchand.",
  username: "Nom d'utilisateur",
  password: "Mot de passe",
  button: "Se connecter",
  loading: "Connexion…",
  forgot: "Mot de passe oublié",
  needHelp: "Besoin d'aide ?",
  showPassword: "Afficher le mot de passe",
  hidePassword: "Masquer le mot de passe",
  networkError: "Erreur réseau. Réessayez.",
  reset: {
    step1Title: "Réinitialiser votre mot de passe",
    step2Title: "Nouveau mot de passe",
    step3Title: "Mot de passe mis à jour",
    stepLabel: (n: number, tot: number) => `Étape ${n} sur ${tot}`,
    step1Intro:
      "Entrez votre nom d'utilisateur ou votre adresse email. Vous recevrez un code à 6 chiffres pour définir un nouveau mot de passe.",
    step2Intro:
      "Nous avons envoyé un code par email si le compte existe. Il expire dans 10 minutes.",
    identLabel: "Nom d'utilisateur ou email",
    identPlaceholder: "votre_username ou vous@example.com",
    usernameLabel: "Nom d'utilisateur",
    usernamePlaceholder: "Nom d'utilisateur du compte",
    codeLabel: "Code reçu",
    newPasswordLabel: "Nouveau mot de passe",
    newPasswordPlaceholder: "Au moins 8 caractères",
    confirmLabel: "Confirmez",
    confirmPlaceholder: "Retapez le mot de passe",
    sendCode: "Envoyer le code",
    confirm: "Confirmer",
    cancel: "Annuler",
    back: "Retour",
    close: "Fermer",
    doneTitle: "Mot de passe changé",
    doneSubtitle: "Reconnectez-vous avec le nouveau.",
    doneCta: "Retour à la connexion",
    mismatch: "Les deux mots de passe ne correspondent pas.",
    tooShort: "Le mot de passe doit contenir au moins 8 caractères.",
    networkError: "Erreur réseau. Réessayez.",
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe",
  },
};

const EN: typeof FR = {
  title: "Sign in",
  subtitle: "Access your merchant dashboard.",
  username: "Username",
  password: "Password",
  button: "Sign in",
  loading: "Signing in…",
  forgot: "Forgot password",
  needHelp: "Need help?",
  showPassword: "Show password",
  hidePassword: "Hide password",
  networkError: "Network error. Please retry.",
  reset: {
    step1Title: "Reset your password",
    step2Title: "New password",
    step3Title: "Password updated",
    stepLabel: (n: number, tot: number) => `Step ${n} of ${tot}`,
    step1Intro:
      "Enter your username or email address. You will receive a 6-digit code to set a new password.",
    step2Intro:
      "We emailed a code if the account exists. It expires in 10 minutes.",
    identLabel: "Username or email",
    identPlaceholder: "your_username or you@example.com",
    usernameLabel: "Username",
    usernamePlaceholder: "Account username",
    codeLabel: "Code",
    newPasswordLabel: "New password",
    newPasswordPlaceholder: "At least 8 characters",
    confirmLabel: "Confirm",
    confirmPlaceholder: "Retype the password",
    sendCode: "Send code",
    confirm: "Confirm",
    cancel: "Cancel",
    back: "Back",
    close: "Close",
    doneTitle: "Password changed",
    doneSubtitle: "Sign in again with the new one.",
    doneCta: "Back to sign in",
    mismatch: "Passwords do not match.",
    tooShort: "Password must be at least 8 characters long.",
    networkError: "Network error. Please retry.",
    showPassword: "Show password",
    hidePassword: "Hide password",
  },
};
